<template>
  <div class="fill-height of-y" style="overflow-x: auto">
    <transition enter-active-class="scale-in-center" leave-active-class="scale-out-center" mode="out-in">
      <div class="c-d-flex c-align-center c-justify-center fill-height" v-if="loading" key="loading">
        <v-progress-circular
            :size="40"
            :width="3"
            color="secondary"
            indeterminate
        ></v-progress-circular>
      </div>
      <div v-else key="loaded" class="fill-height" style="overflow-x: auto">
        <!-- Header with global search -->
        <div class="c-d-flex c-align-center pa-3 pb-2">
          <div class="headline">Chat Settings</div>
          <v-spacer></v-spacer>
          <v-text-field
              v-model="globalSearch"
              placeholder="Search users..."
              prepend-inner-icon="search"
              hide-details
              solo
              flat
              dense
              clearable
              style="max-width: 300px;"
          ></v-text-field>
        </div>

        <!-- Column-based layout -->
        <div class="c-d-flex" style="min-width: min-content; height: calc(100% - 70px);">
          <!-- Column for each model -->
          <div
              v-for="model in activeModels"
              :key="model.id"
              class="model-column"
              style="min-width: 280px; max-width: 280px;"
          >
            <!-- Sticky header -->
            <div class="model-header card-back" style="position: sticky; top: 0; z-index: 2;">
              <v-card flat class="pa-2 card-back">
                <div class="subtitle-1 font-weight-bold text-truncate" :title="model.name">
                  {{ model.name }}
                </div>
                <div class="caption on-surface-grey text-truncate" :title="model.ecosystem">
                  {{ model.ecosystem }}
                </div>
              </v-card>
              <v-divider></v-divider>
            </div>

            <!-- User list for this model -->
            <div class="user-list of-y" style="height: calc(100% - 75px); overflow-y: auto;">
              <v-list dense class="py-0">
                <v-list-tile
                    v-for="user in getFilteredUsersForModel(model.id)"
                    :key="user.id"
                    @click="toggleUserModel(user.id, model.id)"
                    style="cursor: pointer;"
                    :disabled="isSaving(user.id, model.id)"
                >
                  <v-list-tile-action class="mr-2">
                    <template v-if="isSaving(user.id, model.id)">
                      <v-progress-circular
                          :size="20"
                          :width="2"
                          color="primary"
                          indeterminate
                      ></v-progress-circular>
                    </template>
                    <template v-else>
                      <v-checkbox
                          :input-value="isUserAssignedToModel(user.id, model.id)"
                          color="primary"
                          hide-details
                          @click.stop="toggleUserModel(user.id, model.id)"
                      ></v-checkbox>
                    </template>
                  </v-list-tile-action>
                  <v-list-tile-content>
                    <v-list-tile-title class="body-2">
                      {{ user.name }} {{ user.surname }}
                    </v-list-tile-title>
                    <v-list-tile-sub-title class="caption">
                      {{ user.username }}
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  data() {
    return {
      loading: true,
      users: [],
      models: [],
      userModelAssignments: [],
      globalSearch: '',
      savingMap: {}, // Track which user-model combinations are currently saving
    }
  },
  mounted() {
    this.startup()
  },
  methods: {
    async startup() {
      try {
        this.loading = true
        const data = await this.$newReq('GET', 'ai/admin/setup/chatSettings/startup')
        this.users = data.users
        this.models = data.models
        this.userModelAssignments = data.userModelAssignments
      } catch (e) {
        console.log(e)
        this.$snack.networkError()
      } finally {
        this.loading = false
      }
    },

    isUserAssignedToModel(userId, modelId) {
      return this.userModelAssignments.some(
        a => a.fkUserId === userId && a.fkModelId === modelId
      )
    },

    isSaving(userId, modelId) {
      const key = `${userId}-${modelId}`
      return this.savingMap[key] || false
    },

    setSaving(userId, modelId, value) {
      const key = `${userId}-${modelId}`
      this.$set(this.savingMap, key, value)
    },

    async toggleUserModel(userId, modelId) {
      // Prevent double-clicks while saving
      if (this.isSaving(userId, modelId)) {
        return
      }

      const isAssigned = this.isUserAssignedToModel(userId, modelId)

      try {
        this.setSaving(userId, modelId, true)

        let newModelIds = this.userModelAssignments
          .filter(a => a.fkUserId === userId)
          .map(a => a.fkModelId)

        if (isAssigned) {
          // Remove the model
          newModelIds = newModelIds.filter(id => id !== modelId)
        } else {
          // Add the model
          newModelIds.push(modelId)
        }

        await this.$newReq('POST', 'ai/admin/setup/chatSettings/updateUserModels', {
          userId: userId,
          modelIds: newModelIds
        })

        // Update local data
        this.userModelAssignments = this.userModelAssignments.filter(
          a => a.fkUserId !== userId
        )
        newModelIds.forEach(mId => {
          this.userModelAssignments.push({
            fkUserId: userId,
            fkModelId: mId
          })
        })

        // Show success message
        this.$snack.info('Chat model access updated')

      } catch (e) {
        console.log(e)
        this.$snack.error('Failed to update chat model access')
      } finally {
        this.setSaving(userId, modelId, false)
      }
    },

    getFilteredUsersForModel(modelId) {
      const searchTerm = (this.globalSearch || '').toLowerCase()

      return this.users
        .filter(user => {
          if (!searchTerm) return true
          const fullName = `${user.name} ${user.surname}`.toLowerCase()
          const username = user.username.toLowerCase()
          return fullName.includes(searchTerm) || username.includes(searchTerm)
        })
        .sort((a, b) => {
          // Sort by assignment status first (assigned users on top)
          const aAssigned = this.isUserAssignedToModel(a.id, modelId)
          const bAssigned = this.isUserAssignedToModel(b.id, modelId)

          if (aAssigned && !bAssigned) return -1
          if (!aAssigned && bAssigned) return 1

          // Then sort alphabetically by full name
          const aName = `${a.name} ${a.surname}`.toLowerCase()
          const bName = `${b.name} ${b.surname}`.toLowerCase()
          return aName.localeCompare(bName)
        })
    }
  },
  computed: {
    activeModels() {
      return this.models
        .filter(m => m.isActive && m.ecosystem !== 'Local')
        .sort((a, b) => a.name.localeCompare(b.name))
    }
  }
}
</script>

<style scoped>
.model-column {
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}

.theme--dark .model-column {
  border-right: 1px solid rgba(255, 255, 255, 0.12);
}

.on-surface-grey {
  color: var(--on-surface-grey);
}
</style>